import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  CircularProgress, Alert, Box, Typography, List, ListItem, ListItemText, Checkbox, Grid, MenuItem,
  Paper, IconButton, useMediaQuery, useTheme
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import CodeIcon from '@mui/icons-material/Code';

// Helper to convert form state array to the object structure needed by API (from CustomCategoriesSettings.js)
const formCategoriesToObject = (formCats) => {
    return formCats.reduce((acc, cat) => {
        // Ensure category name is not empty and subjects is an array
        if (cat.name.trim() && Array.isArray(cat.subjects)) {
            // Filter out empty subjects
            const validSubjects = cat.subjects.map(s => String(s).trim()).filter(s => s !== ''); // Ensure subjects are strings before trimming
            if (validSubjects.length > 0) {
                acc[cat.name.trim()] = validSubjects;
            }
        }
        return acc;
    }, {});
};

// Helper to convert API object structure to form state array (from CustomCategoriesSettings.js)
const objectToFormCategories = (apiObject) => {
    if (!apiObject || typeof apiObject !== 'object') {
        return [];
    }
    return Object.entries(apiObject).map(([name, subjects], index) => ({
        id: `cat-${index}-${Date.now()}-${Math.random()}`, // Include index for stability
        name: name,
        // Ensure subjects is an array of strings
        subjects: Array.isArray(subjects) ? subjects.map(String) : []
    }));
};

const BulkAddBusinessesModal = ({
  open,
  onClose,
  onBulkAddSuccess,
  API_URL,
  currencies = {}, // Expecting currencies like { "USD": { "name": "US Dollar", "symbol_native": "$" } }
  formatErrorMessage = (e) => String(e), // Default error formatter
  subscriptionStatus = {},
  currentBusinessCount = 0,
  businessLimit = 3
}) => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const [step, setStep] = useState(1); // 1: Search, 2: Select & Input, 3: Summary

  // Step 1: Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoadingSearch, setIsLoadingSearch] = useState(false);
  const [searchError, setSearchError] = useState(null);

  // Step 1.1: Pagination state for search results
  const [nextPageToken, setNextPageToken] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Step 2: Selection and common data state
  const [selectedPlaces, setSelectedPlaces] = useState(new Set()); // Store place_ids
  const [commonTurnover, setCommonTurnover] = useState('');
  const [commonCurrency, setCommonCurrency] = useState('USD'); // Default currency
  const [formCategories, setFormCategories] = useState([]); // State for custom categories
  const [categoriesJson, setCategoriesJson] = useState('{}'); // State for JSON editor
  const [jsonValidationError, setJsonValidationError] = useState(null); // State for JSON validation errors
  const [showJsonEditor, setShowJsonEditor] = useState(false); // State to toggle JSON editor visibility

  // Step 3: Adding and summary state
  const [isAdding, setIsAdding] = useState(false);
  const [addError, setAddError] = useState(null);
  const [bulkAddResult, setBulkAddResult] = useState(null);

  // NEW: Existing businesses state
  const [existingBusinesses, setExistingBusinesses] = useState([]);
  const [existingPlaceIds, setExistingPlaceIds] = useState(new Set());
  const [isLoadingExisting, setIsLoadingExisting] = useState(false);

  const currencyList = Object.entries(currencies)
    .map(([code, details]) => ({ code, name: details.name, symbol: details.symbol_native }))
    .sort((a, b) => a.name.localeCompare(b.name));
  
  // NEW: Function to fetch existing businesses
  const fetchExistingBusinesses = useCallback(async () => {
    setIsLoadingExisting(true);
    try {
      const response = await fetch(`${API_URL}/businesses/`, {
        credentials: 'include',
      });
      if (response.ok) {
        const businesses = await response.json();
        setExistingBusinesses(businesses);
        const placeIds = new Set(businesses.map(b => b.place_id).filter(Boolean));
        setExistingPlaceIds(placeIds);
      } else {
        console.error('Failed to fetch existing businesses');
        setExistingBusinesses([]);
        setExistingPlaceIds(new Set());
      }
    } catch (error) {
      console.error('Error fetching existing businesses:', error);
      setExistingBusinesses([]);
      setExistingPlaceIds(new Set());
    } finally {
      setIsLoadingExisting(false);
    }
  }, [API_URL]);

  useEffect(() => {
    // Reset state when modal is opened, but not if it's just re-rendering while open
    if (open) {
        // Optional: intelligently preserve some state if desired, for now full reset
        setStep(1);
        setSearchTerm('');
        setSearchResults([]);
        setIsLoadingSearch(false);
        setSearchError(null);
        setNextPageToken(null); // Reset pagination token
        setIsLoadingMore(false); // Reset loading more state
        setSelectedPlaces(new Set());
        setCommonTurnover('');
        // Don't reset commonCurrency if a global default is preferred
        // setCommonCurrency('USD'); 
        setFormCategories([]); // Reset categories
        setCategoriesJson('{}'); // Reset JSON categories
        setJsonValidationError(null); // Reset JSON error
        setShowJsonEditor(false); // Reset JSON editor visibility
        setIsAdding(false);
        setAddError(null);
        setBulkAddResult(null);
        
        // NEW: Fetch existing businesses when modal opens
        fetchExistingBusinesses();
    }
  }, [open, fetchExistingBusinesses]);

  // Sync Form State -> JSON State (Adapted from CustomCategoriesSettings.js)
  useEffect(() => {
    try {
        const newJsonObject = formCategoriesToObject(formCategories);
        const newJsonString = JSON.stringify(newJsonObject, null, 2);
        setCategoriesJson(newJsonString);
        setJsonValidationError(null); // Clear validation error if conversion is successful
    } catch (e) {
        console.error("Error converting form state to JSON:", e);
        setJsonValidationError("Internal error converting form data to display in JSON editor.");
    }
}, [formCategories]);

  // Handle JSON Text Field Change (Manual Edit - Adapted from CustomCategoriesSettings.js)
  const handleJsonChange = (event) => {
    const newJsonString = event.target.value;
    setCategoriesJson(newJsonString);
    try {
        const parsed = JSON.parse(newJsonString);
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
            throw new Error("Input must be a valid JSON object (key-value pairs).");
        }
        // Basic validation for category structure
        for (const key in parsed) {
            if (!Array.isArray(parsed[key]) || !parsed[key].every(item => typeof item === 'string')) {
                throw new Error("Each category must have a name (string key) and a list of subjects (array of strings).");
            }
        }
        setFormCategories(objectToFormCategories(parsed)); // Sync JSON -> Form if valid
        setJsonValidationError(null); // Clear validation error
        setAddError(null); // Clear general add error as well if JSON becomes valid
    } catch (parseError) {
        console.error("JSON Parse Error on manual edit:", parseError);
        setJsonValidationError(`Invalid JSON: ${parseError.message}`);
        // Don't clear the text field, let user fix it
        // Potentially clear formCategories if JSON is fundamentally broken?
        // For now, formCategories will retain its last valid state from JSON.
    }
  };

  const handleSearchTermChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleSearchClick = async () => {
    if (!searchTerm.trim()) {
      setSearchError("Please enter a business name to search.");
      return;
    }
    setIsLoadingSearch(true);
    setSearchError(null);
    setSearchResults([]);
    setSelectedPlaces(new Set()); // Clear previous selections on new search
    setNextPageToken(null); // Reset token for a new search

    try {
      // Initial search does not use a pagetoken
      const response = await fetch(`${API_URL}/businesses/search-google?business_name=${encodeURIComponent(searchTerm)}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to search for businesses.' }));
        throw new Error(formatErrorMessage(errorData));
      }
      const data = await response.json(); // Expect { results: [], next_page_token: "..." }
      
      if (data && data.results && data.results.length > 0) {
        setSearchResults(data.results);
        setNextPageToken(data.next_page_token || null);
        setStep(2); // Move to next step if results are found
      } else {
        setSearchError('No businesses found matching your search term.');
        setSearchResults([]); // Ensure it's an empty array
        setNextPageToken(null);
      }
    } catch (error) {
      console.error("Search error:", error);
      setSearchError(error.message || 'An unexpected error occurred during search.');
      setSearchResults([]);
    } finally {
      setIsLoadingSearch(false);
    }
  };

  const handleLoadMore = async () => {
    if (!nextPageToken || isLoadingMore || isLoadingSearch) return;

    setIsLoadingMore(true);
    setSearchError(null); // Clear previous search errors specific to initial search

    try {
      const response = await fetch(`${API_URL}/businesses/search-google?business_name=${encodeURIComponent(searchTerm)}&pagetoken=${nextPageToken}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to load more businesses.' }));
        throw new Error(formatErrorMessage(errorData));
      }
      const data = await response.json(); // Expect { results: [], next_page_token: "..." }

      if (data && data.results && data.results.length > 0) {
        setSearchResults(prevResults => [...prevResults, ...data.results]);
        setNextPageToken(data.next_page_token || null);
      } else {
        // If no results, or data.results is empty, it means no more can be loaded.
        setNextPageToken(null); // No more pages to load
      }
    } catch (error) {
      console.error("Load more error:", error);
      // Keep existing results, but show an error specific to loading more
      setSearchError(error.message || 'An unexpected error occurred while loading more results.');
      setNextPageToken(null); // Stop further attempts if an error occurs
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleCloseModal = () => {
    // Do not reset internal state here directly, rely on useEffect when 'open' prop changes
    onClose(); 
  };

  const handleToggleSelectPlace = (placeId) => {
    // NEW: Prevent selection of already-added businesses
    if (existingPlaceIds.has(placeId)) {
      return; // Don't allow toggling if business is already added
    }
    
    setSelectedPlaces(prevSelected => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(placeId)) {
        newSelected.delete(placeId);
      } else {
        // Check if adding this business would exceed the limit (skip check for admin users)
        const potentialNewTotal = currentBusinessCount + newSelected.size + 1;
        if (potentialNewTotal > businessLimit && subscriptionStatus.user_role !== 'admin') {
          setAddError(`Adding this business would exceed your plan limit of ${businessLimit} businesses. You currently have ${currentBusinessCount} businesses. Please upgrade your plan or remove some businesses first.`);
          return prevSelected; // Don't add the business
        }
        newSelected.add(placeId);
        setAddError(null); // Clear any previous limit errors
      }
      return newSelected;
    });
  };

  const handleSelectAllResults = (event) => {
    if (event.target.checked) {
      // NEW: Only select businesses that are not already added
      const availablePlaces = searchResults.filter(p => !existingPlaceIds.has(p.place_id));
      
      // Check how many we can actually select within the limit (skip for admins)
      if (subscriptionStatus.user_role !== 'admin') {
        const remainingSlots = businessLimit - currentBusinessCount;
        
        if (remainingSlots <= 0) {
          setAddError(`You've reached your plan limit of ${businessLimit} businesses. Please upgrade your plan or remove some businesses first.`);
          return;
        }
        
        if (availablePlaces.length > remainingSlots) {
          // Select only up to the limit without showing redundant error
          const placesToSelect = availablePlaces.slice(0, remainingSlots).map(p => p.place_id);
          setSelectedPlaces(new Set(placesToSelect));
          setAddError(null); // Clear any previous errors
        } else {
          const availablePlaceIds = new Set(availablePlaces.map(p => p.place_id));
          setSelectedPlaces(availablePlaceIds);
          setAddError(null); // Clear any previous errors
        }
      } else {
        // Admin users can select all available places
        const availablePlaceIds = new Set(availablePlaces.map(p => p.place_id));
        setSelectedPlaces(availablePlaceIds);
        setAddError(null); // Clear any previous errors
      }
    } else {
      setSelectedPlaces(new Set());
      setAddError(null); // Clear any previous errors
    }
  };

  // --- Category Handlers (Simplified from CustomCategoriesSettings.js) ---
  const handleAddCategory = () => {
    setFormCategories(prev => [
        ...prev,
        // Use placeholder text similar to CustomCategoriesSettings for button-triggered add
        { id: `cat-${Date.now()}-${Math.random()}`, name: 'New Category', subjects: ['New Subject'] }
    ]);
    setJsonValidationError(null); // Clear JSON error if user is using form editor
    setAddError(null); // Clear general add error
  };

  const handleRemoveCategory = (idToRemove) => {
      setFormCategories(prev => prev.filter(cat => cat.id !== idToRemove));
  };

  const handleCategoryNameChange = (idToUpdate, newName) => {
      setFormCategories(prev => prev.map(cat =>
          cat.id === idToUpdate ? { ...cat, name: newName } : cat
      ));
  };

  const handleAddSubject = (categoryId) => {
      setFormCategories(prev => prev.map(cat =>
          // Use placeholder text similar to CustomCategoriesSettings
          cat.id === categoryId ? { ...cat, subjects: [...cat.subjects, 'New Subject'] } : cat
      ));
      setJsonValidationError(null);
      setAddError(null);
  };

  const handleRemoveSubject = (categoryId, subjectIndexToRemove) => {
      setFormCategories(prev => prev.map(cat =>
          cat.id === categoryId
              ? { ...cat, subjects: cat.subjects.filter((_, index) => index !== subjectIndexToRemove) }
              : cat
      ));
  };

  const handleSubjectNameChange = (categoryId, subjectIndexToUpdate, newSubjectName) => {
      setFormCategories(prev => prev.map(cat =>
          cat.id === categoryId
              ? { ...cat, subjects: cat.subjects.map((subj, index) => index === subjectIndexToUpdate ? newSubjectName : subj) }
              : cat
      ));
  };
  // --- End Category Handlers ---

  // --- Enter Key Navigation and Adding (Adapted from CustomCategoriesSettings.js) ---
  const handleCategoryKeyDown = (event, categoryId, catIndex) => {
      if (event.key === 'Enter') {
          event.preventDefault();
          event.stopPropagation();
          const firstSubjectId = `modal-subject-input-${catIndex}-0`;
          const placeholderSubjectId = `modal-subject-input-placeholder-${catIndex}`;
          const targetElement = document.getElementById(firstSubjectId) || document.getElementById(placeholderSubjectId);
          targetElement?.focus();
      }
  };

  const handleAddCategoryKeyDown = (event) => {
      if (event.key === 'Enter' && event.target.value.trim() !== '') {
          event.preventDefault();
          event.stopPropagation();
          const newCategoryName = event.target.value.trim();
          const newCategoryId = `cat-${formCategories.length}-${Date.now()}-${Math.random()}`;
          setFormCategories(prev => [
              ...prev,
              { id: newCategoryId, name: newCategoryName, subjects: [] } // Add with empty subject list initially
          ]);
          setTimeout(() => {
              document.getElementById(`modal-category-input-${formCategories.length}`)?.focus(); // Focus the newly added category name (now at the new length index)
          }, 0);
          event.target.value = ''; // Clear the placeholder input
          setJsonValidationError(null);
          setAddError(null);
      }
  };

  const handleSubjectKeyDown = (event, categoryId, catIndex, subIndex) => {
      if (event.key === 'Enter') {
          event.preventDefault();
          event.stopPropagation();
          const currentCategory = formCategories.find(c => c.id === categoryId);
          if (!currentCategory) return;

          const isLastSubject = subIndex === currentCategory.subjects.length - 1;

          if (!isLastSubject) {
              const nextSubjectId = `modal-subject-input-${catIndex}-${subIndex + 1}`;
              document.getElementById(nextSubjectId)?.focus();
          } else {
              const addSubjectPlaceholderId = `modal-subject-input-placeholder-${catIndex}`;
              document.getElementById(addSubjectPlaceholderId)?.focus();
          }
      }
  };

  const handleAddSubjectKeyDown = (event, categoryId, catIndex) => {
      if (event.key === 'Enter') {
          event.preventDefault();
          event.stopPropagation();
          const newSubjectName = event.target.value.trim();

          if (newSubjectName !== '') {
              setFormCategories(prev => prev.map(cat =>
                  cat.id === categoryId ? { ...cat, subjects: [...cat.subjects, newSubjectName] } : cat
              ));
              event.target.value = ''; // Clear the placeholder input
              setJsonValidationError(null);
              setAddError(null);
              // Focus stays on the add subject placeholder for this category
              setTimeout(() => document.getElementById(`modal-subject-input-placeholder-${catIndex}`)?.focus(), 0);
          } else {
              // If Enter is pressed in an empty "Add Subject" placeholder, move to next category's placeholder or "Add New Category" input
              const nextCategoryInputId = `modal-category-input-${catIndex + 1}`;
              const addCategoryPlaceholderId = `modal-category-input-placeholder`;
              const targetElement = document.getElementById(nextCategoryInputId) || document.getElementById(addCategoryPlaceholderId);
              targetElement?.focus();
          }
      }
  };
  // --- End Enter Key Handlers ---

  const handleBulkAddSubmit = async () => {
    if (selectedPlaces.size === 0) {
        setAddError("Please select at least one business to add.");
        return;
    }
    
    // Check business limit before other validations (skip for admin users)
    const potentialNewTotal = currentBusinessCount + selectedPlaces.size;
    if (potentialNewTotal > businessLimit && subscriptionStatus.user_role !== 'admin') {
        setAddError(`Adding ${selectedPlaces.size} business${selectedPlaces.size === 1 ? '' : 'es'} would exceed your plan limit of ${businessLimit}. You currently have ${currentBusinessCount} businesses. Please select fewer businesses or upgrade your plan.`);
        return;
    }
    
    if (!commonTurnover.trim() || !commonCurrency) {
        setAddError("Please provide turnover and currency for the selected businesses.");
        return;
    }

    // Mandatory Category Validation
    if (jsonValidationError) { // If JSON is invalid, block submission
        setAddError("Custom categories JSON is invalid. Please correct it before submitting.");
        return;
    }
    const finalCategoriesObject = formCategoriesToObject(formCategories); // Use the helper to get clean object
    if (Object.keys(finalCategoriesObject).length === 0) {
        setAddError("Custom categories are mandatory. Please define at least one category with at least one subject.");
        return;
    }
    for (const categoryName in finalCategoriesObject) {
        if (!finalCategoriesObject[categoryName] || finalCategoriesObject[categoryName].length === 0) {
            setAddError(`Category '${categoryName}' must have at least one non-empty subject.`);
            return;
        }
        // The formCategoriesToObject already filters empty subjects, so this check might be redundant
        // but kept for explicit validation message if somehow an empty subject list passes.
    }
    // Clear any previous add error if validation passes now
    setAddError(null); 

    setIsAdding(true);
    setBulkAddResult(null);

    const businessesToAdd = searchResults
        .filter(p => selectedPlaces.has(p.place_id))
        .map(p => ({
            place_id: p.place_id,
            name: p.name,
            address: p.formatted_address,
            turnover: parseInt(commonTurnover, 10),
            currency: commonCurrency,
        }));
    
    const requestBody = {
        businesses: businessesToAdd,
    };
    requestBody.categories = finalCategoriesObject; // Use the validated and cleaned object

    try {
        const response = await fetch(`${API_URL}/businesses/bulk-add`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(requestBody),
        });

        const responseData = await response.json();
        if (!response.ok) {
            throw new Error(formatErrorMessage(responseData.detail || responseData));
        }
        
        setBulkAddResult(responseData);
        setStep(3); // Move to summary step
        if (responseData.successfully_added && responseData.successfully_added.length > 0) {
            onBulkAddSuccess(); // Callback to refresh parent list or other actions
        }

    } catch (error) {
        console.error("Bulk add error:", error);
        setAddError(error.message || 'An unexpected error occurred while adding businesses.');
    } finally {
        setIsAdding(false);
    }
  };

  const renderStepContent = () => {
    if (step === 1) {
      return (
        <Box>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Enter the name of the business you want to find locations for (e.g., "Starbucks Seattle").
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            id="business-search-term"
            label="Business Name"
            type="text"
            fullWidth
            variant="outlined"
            value={searchTerm}
            onChange={handleSearchTermChange}
            disabled={isLoadingSearch}
            onKeyPress={(ev) => {
              if (ev.key === 'Enter') {
                handleSearchClick();
                ev.preventDefault();
              }
            }}
          />
          {isLoadingSearch && <CircularProgress size={24} sx={{ mt: 1 }} />}
          {searchError && <Alert severity="error" sx={{ mt: 2 }}>{searchError}</Alert>}
        </Box>
      );
    }
    // Placeholder for other steps
    if (step === 2) {
      if (searchResults.length === 0) {
        return (
            <Box sx={{textAlign: 'center', py: 3}}>
                <Typography variant="subtitle1">No businesses found.</Typography>
                <Button onClick={() => setStep(1)} sx={{mt: 1}}>Search Again</Button>
            </Box>
        )
      }
      return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Select Businesses & Provide Details
            </Typography>
            
            {/* Consolidated Info Alert */}
            <Alert 
                severity="info" 
                sx={{ mb: 2 }}
            >
                {isLoadingExisting ? (
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                        Checking for duplicates...
                    </Box>
                ) : (
                    <>
                        <Typography component="div" variant="body2" sx={{ mb: 1 }}>
                            <strong>Found {searchResults.length} potential business{searchResults.length === 1 ? '' : 'es'}.</strong> 
                            {subscriptionStatus.user_role === 'admin' ? (
                                <> You currently have {currentBusinessCount} businesses monitored.</>
                            ) : (
                                <>
                                    {' '}Business Limit: {currentBusinessCount} of {businessLimit} used
                                    {currentBusinessCount >= businessLimit ? (
                                        <> - You've reached your limit. Please upgrade your plan to add more businesses.</>
                                    ) : (
                                        <> - You can add {businessLimit - currentBusinessCount} more business{businessLimit - currentBusinessCount === 1 ? '' : 'es'}.</>
                                    )}
                                </>
                            )}
                        </Typography>
                        
                        {selectedPlaces.size > 0 && (
                            <Typography component="div" variant="body2" sx={{ mb: 1 }}>
                                <strong>Selected:</strong> {selectedPlaces.size} business{selectedPlaces.size === 1 ? '' : 'es'} 
                                {subscriptionStatus.user_role === 'admin' ? (
                                    <> (New total would be: {currentBusinessCount + selectedPlaces.size} businesses)</>
                                ) : (
                                    <> (New total would be: {currentBusinessCount + selectedPlaces.size}/{businessLimit})</>
                                )}
                            </Typography>
                        )}
                        
                        <Typography component="div" variant="body2">
                            Select businesses to add and provide their estimated annual turnover, currency, and custom categories.
                            {existingPlaceIds.size > 0 && searchResults.some(p => existingPlaceIds.has(p.place_id)) && (
                                <> Businesses already added are marked and cannot be selected again.</>
                            )}
                        </Typography>
                    </>
                )}
            </Alert>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Checkbox 
                    indeterminate={selectedPlaces.size > 0 && selectedPlaces.size < searchResults.filter(p => !existingPlaceIds.has(p.place_id)).length}
                    checked={searchResults.filter(p => !existingPlaceIds.has(p.place_id)).length > 0 && selectedPlaces.size === searchResults.filter(p => !existingPlaceIds.has(p.place_id)).length}
                    onChange={handleSelectAllResults}
                    disabled={searchResults.filter(p => !existingPlaceIds.has(p.place_id)).length === 0}
                />
                <Typography variant="subtitle2">
                    Select All ({selectedPlaces.size} selected of {searchResults.filter(p => !existingPlaceIds.has(p.place_id)).length} available)
                    {existingPlaceIds.size > 0 && searchResults.some(p => existingPlaceIds.has(p.place_id)) && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            • {searchResults.filter(p => existingPlaceIds.has(p.place_id)).length} already added
                        </Typography>
                    )}
                </Typography>
            </Box>
            <List sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #ddd', borderRadius: '4px', mb: 2 }}>
            {searchResults.map((place) => {
                const isAlreadyAdded = existingPlaceIds.has(place.place_id);
                const isSelected = selectedPlaces.has(place.place_id);
                
                return (
                    <ListItem 
                        key={place.place_id} 
                        dense 
                        button={!isAlreadyAdded}
                        onClick={() => handleToggleSelectPlace(place.place_id)}
                        sx={{ 
                            backgroundColor: isSelected ? 'action.hover' : 'transparent',
                            borderBottom: '1px solid #eee',
                            opacity: isAlreadyAdded ? 0.5 : 1,
                            cursor: isAlreadyAdded ? 'default' : 'pointer',
                            '&:hover': {
                                backgroundColor: isAlreadyAdded ? 'transparent' : isSelected ? 'action.hover' : 'action.selected'
                            }
                        }}
                    >
                    <Checkbox
                        edge="start"
                        checked={isSelected}
                        disabled={isAlreadyAdded}
                        tabIndex={-1}
                        disableRipple
                    />
                    <ListItemText 
                        primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {place.name}
                                {isAlreadyAdded && (
                                    <Typography 
                                        variant="caption" 
                                        sx={{ 
                                            backgroundColor: 'success.light',
                                            color: 'success.contrastText',
                                            px: 1,
                                            py: 0.25,
                                            borderRadius: '12px',
                                            fontSize: '0.7rem',
                                            fontWeight: 'medium'
                                        }}
                                    >
                                        Already Added
                                    </Typography>
                                )}
                            </Box>
                        }
                        secondary={place.formatted_address || 'Address not available'} 
                    />
                    </ListItem>
                );
            })}
            </List>
            {/* Move Load More Button here, directly below the results list */}
            {nextPageToken && step === 2 && (
                <Box sx={{ textAlign: 'center', mt: 1, mb: 2 }}>
                    <Button 
                        onClick={handleLoadMore}
                        disabled={isLoadingMore || isLoadingSearch}
                        variant="outlined"
                    >
                        {isLoadingMore ? <CircularProgress size={24} /> : 'Load More Results'}
                    </Button>
                </Box>
            )}
            <Grid container spacing={2} sx={{mt: 1, mb: 3}}>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Common Annual Turnover"
                        type="number"
                        value={commonTurnover}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (val === '' || (/^\d+$/.test(val) && parseInt(val) >= 0)) {
                                setCommonTurnover(val);
                            }
                        }}
                        fullWidth
                        required
                        disabled={isAdding}
                        inputProps={{ min: 0 }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Currency"
                        select
                        value={commonCurrency}
                        onChange={(e) => setCommonCurrency(e.target.value)}
                        fullWidth
                        required
                        disabled={isAdding}
                    >
                        {currencyList.map((option) => (
                            <MenuItem key={option.code} value={option.code}>
                                {option.code} - {option.name}
                            </MenuItem>
                        ))}
                        {currencyList.length === 0 && <MenuItem disabled>Loading currencies...</MenuItem>}
                    </TextField>
                </Grid>
            </Grid>
            
            {/* Custom Categories Section - Now with Grid Layout */}
            <Box sx={{ mt: 1, borderTop: '1px solid #ddd', pt: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
                    Categories
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Input the products or services that your business offers, grouped by category, e.g. (Desserts: Ice Cream, Cake, Pie). These custom definitions will be used to group analysis insights.
                </Typography>
                {jsonValidationError && <Alert severity="warning" sx={{ mb: 2 }}>{jsonValidationError}</Alert>}

                <Grid container spacing={3}>
                    <Grid item xs={12} md={showJsonEditor && subscriptionStatus.user_role === 'admin' ? 6 : 12} sx={{ display: 'flex', flexDirection: 'column' }}> {/* Form-based editor */}
                        <Paper sx={{
                            p: 1.5, 
                            background: 'rgba(255,255,255,0.8)', 
                            height: '300px', // Fixed height for scroll 
                            overflowY: 'auto' 
                        }} elevation={0} variant="outlined">
                            <List dense disablePadding>
                                {formCategories.map((category, catIndex) => (
                                    <Paper key={category.id} variant="outlined" sx={{ mb: 2, p: 1.5, background: 'rgba(255,255,255,0.6)' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                            <TextField
                                                id={`modal-category-input-${catIndex}`}
                                                fullWidth
                                                variant="standard"
                                                size="small"
                                                value={category.name}
                                                onChange={(e) => handleCategoryNameChange(category.id, e.target.value)}
                                                onKeyDown={(e) => handleCategoryKeyDown(e, category.id, catIndex)}
                                                placeholder={`Category ${catIndex + 1} Name`}
                                                sx={{ mr: 1, '.MuiInput-input': { fontWeight: 'bold' } }}
                                                error={!category.name.trim()} // Basic validation feedback
                                                inputProps={{ enterKeyHint: 'next' }}
                                            />
                                            <IconButton onClick={() => handleRemoveCategory(category.id)} size="small" color="error" title="Remove Category">
                                                <RemoveCircleOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                        <List dense disablePadding sx={{ pl: 2 }}>
                                            {category.subjects.map((subject, subIndex) => (
                                                <ListItem key={`${category.id}-subj-${subIndex}`} disableGutters sx={{mb: 0.5}}>
                                                    <TextField
                                                        id={`modal-subject-input-${catIndex}-${subIndex}`}
                                                        fullWidth
                                                        variant="standard"
                                                        size="small"
                                                        value={subject}
                                                        onChange={(e) => handleSubjectNameChange(category.id, subIndex, e.target.value)}
                                                        onKeyDown={(e) => handleSubjectKeyDown(e, category.id, catIndex, subIndex)}
                                                        placeholder={`Subject ${subIndex + 1}`}
                                                        sx={{ mr: 1 }}
                                                        error={!String(subject).trim()} // Basic validation feedback
                                                        inputProps={{ enterKeyHint: 'next' }}
                                                    />
                                                    <IconButton onClick={() => handleRemoveSubject(category.id, subIndex)} size="small" color="error" title="Remove Subject">
                                                        <RemoveCircleOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </ListItem>
                                            ))}
                                            <ListItem disableGutters sx={{mb: 0.5}}>
                                                <TextField
                                                    id={`modal-subject-input-placeholder-${catIndex}`}
                                                    fullWidth
                                                    variant="standard"
                                                    size="small"
                                                    placeholder="Add New Subject... (Press Enter)"
                                                    onKeyDown={(e) => handleAddSubjectKeyDown(e, category.id, catIndex)}
                                                    sx={{ mr: 1, '.MuiInput-input': { fontStyle: 'italic' } }}
                                                    inputProps={{ enterKeyHint: 'next' }}
                                                />
                                            </ListItem>
                                        </List>
                                    </Paper>
                                ))}
                            </List>
                            <Paper variant="outlined" sx={{ p: 1.5, background: 'rgba(245,245,255,0.4)', borderStyle: 'dashed' }}>
                                <TextField
                                    id={`modal-category-input-placeholder`}
                                    fullWidth
                                    variant="standard"
                                    size="small"
                                    placeholder="Add New Category... (Press Enter)"
                                    onKeyDown={handleAddCategoryKeyDown}
                                    sx={{ '.MuiInput-input': { fontStyle: 'italic' } }}
                                    inputProps={{ enterKeyHint: 'next' }}
                                />
                            </Paper>
                        </Paper>
                        
                        {/* JSON Toggle Button - Available on all screen sizes but only for admin users */}
                        {subscriptionStatus.user_role === 'admin' && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<CodeIcon />}
                                onClick={() => setShowJsonEditor(!showJsonEditor)}
                                sx={{ 
                                    color: '#1976d2',
                                    borderColor: '#1976d2',
                                    '&:hover': {
                                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                        borderColor: '#1976d2'
                                    }
                                }}
                            >
                                {showJsonEditor ? 'Hide JSON Editor' : 'Show JSON Editor'}
                            </Button>
                        </Box>
                        )}
                    </Grid>
                    
                    {/* JSON Editor - Controlled by toggle button and only for admin users */}
                    {showJsonEditor && subscriptionStatus.user_role === 'admin' && (
                        <Grid item xs={12} md={6}> {/* JSON editor */}
                            <TextField
                                label="Edit as JSON"
                                multiline
                                rows={12} // Adjust rows for similar height to form editor with its padding
                                value={categoriesJson}
                                onChange={handleJsonChange}
                                variant="outlined"
                                fullWidth
                                error={!!jsonValidationError}
                                // helperText={jsonValidationError || "Enter categories as a JSON object, e.g., { \"Category1\": [\"Subject1\"] }"}
                                sx={{ 
                                    fontFamily: 'monospace', 
                                    '& .MuiInputBase-root': { height: '100%' }, 
                                    '& .MuiInputBase-inputMultiline': { height: '100% !important', overflowY: 'auto !important' }
                                }}
                            />
                        </Grid>
                    )}
                </Grid>
            </Box>

            {addError && <Alert severity="error" sx={{ mt: 2 }}>{addError}</Alert>} {/* Moved here to be below categories */}

            {/* Load More Button and Indicator for search results list */} 
            {searchError && step === 2 && !isLoadingMore && <Alert severity="warning" sx={{ mt: 2 }}>{searchError}</Alert>} {/* Display search/load more errors here too */} 
        </Box>
      );
    }
    if (step === 3) {
        if (isAdding) { // Should ideally not happen if logic is correct, but as a fallback
            return <Box sx={{textAlign: 'center', py: 3}}><CircularProgress /><Typography sx={{mt:1}}>Processing...</Typography></Box>;
        }
        if (!bulkAddResult) {
            return (
                <Box sx={{textAlign: 'center', py: 3}}>
                    <Typography variant="subtitle1">No summary available yet.</Typography>
                    {addError && <Alert severity="error" sx={{mt:1}}>{addError}</Alert>}
                    <Button onClick={() => setStep(selectedPlaces.size > 0 ? 2 : 1)} sx={{mt:1}}>Go Back</Button>
                </Box>
            );
        }

        const { successfully_added = [], failed_to_add = [] } = bulkAddResult;

        return (
            <Box>
                <Typography variant="h6" gutterBottom>Bulk Add Summary</Typography>
                {addError && <Alert severity="error" sx={{ mb: 2 }}>{addError}</Alert>} {/* Global error for the operation itself */}
                
                {successfully_added.length > 0 && (
                    <Box mb={3}>
                        <Typography variant="subtitle1" color="success.main" gutterBottom>
                            Successfully Added ({successfully_added.length})
                        </Typography>
                        <List dense sx={{maxHeight: 200, overflow: 'auto', border: '1px solid green', borderRadius: 1}}>
                            {successfully_added.map((item, index) => (
                                <ListItem key={item.id || index} sx={{borderBottom: '1px solid #eee'}}>
                                    <ListItemText primary={item.name} secondary={item.address} />
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )}

                {failed_to_add.length > 0 && (
                    <Box>
                        <Typography variant="subtitle1" color="error.main" gutterBottom>
                            Failed to Add ({failed_to_add.length})
                        </Typography>
                        <List dense sx={{maxHeight: 200, overflow: 'auto', border: '1px solid red', borderRadius: 1}}>
                            {failed_to_add.map((item, index) => (
                                <ListItem key={item.place_id || index} sx={{borderBottom: '1px solid #eee'}}>
                                    <ListItemText 
                                        primary={item.name} 
                                        secondary={`Error: ${item.error_detail}`}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )}

                {(successfully_added.length === 0 && failed_to_add.length === 0 && !addError) && (
                    <Typography>No businesses were processed, or the result is empty.</Typography>
                )}
            </Box>
        );
    }
    return null;
  };

  const renderDialogActions = () => {
    if (step === 1) {
      return (
        <>
          <Button onClick={handleCloseModal} color="secondary">Cancel</Button>
          <Button 
            onClick={handleSearchClick} 
            variant="contained" 
            disabled={isLoadingSearch || !searchTerm.trim()}
            startIcon={isLoadingSearch ? <CircularProgress size={16} color="inherit"/> : <SearchIcon />}
          >
            Search
          </Button>
        </>
      );
    }
    if (step === 2) {
        const isCategoryDataValid = () => {
            if (jsonValidationError) return false;
            const catsObj = formCategoriesToObject(formCategories);
            if (Object.keys(catsObj).length === 0) return false;
            for (const catName in catsObj) {
                if (!catsObj[catName] || catsObj[catName].length === 0) return false;
            }
            return true;
        };
        const categoriesValid = isCategoryDataValid();

        return (
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                {/* Left-aligned help text */}
                <Box sx={{ flex: 1, textAlign: 'left' }}>
                    <Typography variant="body2" color="text.secondary">
                        Need help? <a href="mailto:info@sashy.ai" style={{ color: '#1976d2', textDecoration: 'underline' }}>info@sashy.ai</a>
                    </Typography>
                </Box>
                {/* Right-aligned actions */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button onClick={() => setStep(1)} color="secondary" disabled={isAdding}>Back to Search</Button>
                    <Button 
                        onClick={handleBulkAddSubmit}
                        variant="contained"
                        disabled={isAdding || selectedPlaces.size === 0 || !commonTurnover.trim() || !commonCurrency || !categoriesValid}
                        startIcon={isAdding ? <CircularProgress size={16} color="inherit"/> : null}
                    >
                        Add {selectedPlaces.size > 0 ? `${selectedPlaces.size} Selected` : 'Businesses'}
                    </Button>
                </Box>
            </Box>
        )
    }
    // Placeholder for other steps
    if (step === 3) {
        return (
            <>
                <Button onClick={() => { 
                    setStep(1); // Reset to search step for another bulk add
                    setSearchTerm('');
                    setSearchResults([]);
                    setSelectedPlaces(new Set());
                    // Keep turnover/currency or reset as desired
                    // setCommonTurnover(''); 
                    // setCommonCurrency('USD');
                    setBulkAddResult(null);
                    setAddError(null);
                 }} color="primary">
                    Add More Businesses
                </Button>
                <Button onClick={handleCloseModal} variant="contained">
                    Done
                </Button>
            </>
        );
    }
    return <Button onClick={handleCloseModal}>Close</Button>;
  };

  return (
    <Dialog open={open} onClose={handleCloseModal} fullWidth maxWidth="md">
      <DialogTitle>
        {step === 1 && 'Search for Businesses to Add'}
        {step === 2 && `Select Businesses for "${searchTerm}"`}
        {step === 3 && 'Bulk Add Summary'}
      </DialogTitle>
      <DialogContent dividers>
        {renderStepContent()}
      </DialogContent>
      <DialogActions sx={{p: {xs: 2, sm: 3}}}>
        {renderDialogActions()}
      </DialogActions>
    </Dialog>
  );
};

export default BulkAddBusinessesModal; 